import { Dialog } from '../components/Dialog';
import type { GameStore } from '../../store/GameStore';

/**
 * Dialog for creating a new team.
 * Simple form: team name input + create button.
 * Auto-assigns the CEO if they're not on a team.
 */
export function openCreateTeamDialog(parent: HTMLElement, store: GameStore, onClose: () => void): void {
  const state = store.getState();
  const prevSpeed = state.speed;
  store.dispatch({ type: 'SET_SPEED', speed: 0 });

  const nameInput: HTMLInputElement = document.createElement('input');
  const errorEl: HTMLDivElement = document.createElement('div');

  const dialog = new Dialog({
    title: 'Create Team',
    onClose: () => {
      store.dispatch({ type: 'SET_SPEED', speed: prevSpeed });
      onClose();
    },
    actions: [
      {
        label: 'Create',
        style: 'primary',
        onClick: () => {
          const name = nameInput.value.trim();
          if (!name) {
            errorEl.textContent = 'Enter a team name.';
            return;
          }
          const result = store.dispatch({ type: 'CREATE_TEAM', name });
          if (!result.success) {
            errorEl.textContent = result.error ?? 'Failed to create team.';
            return;
          }

          // Auto-assign CEO if not on a team
          const s = store.getState();
          const ceo = s.staff.find((st) => st.isCEO);
          const newTeam = s.teams[s.teams.length - 1];
          if (ceo && !ceo.teamId && newTeam) {
            store.dispatch({ type: 'ASSIGN_TO_TEAM', staffId: ceo.id, teamId: newTeam.id });
          }

          dialog.close();
        },
      },
    ],
  });

  // Form content
  const form = document.createElement('div');
  form.style.cssText = 'display:flex;flex-direction:column;gap:12px;';

  const label = document.createElement('label');
  label.textContent = 'Team Name';
  label.style.cssText = 'font-size:12px;color:var(--color-text-secondary);';

  nameInput.type = 'text';
  nameInput.value = `Team ${state.teams.length + 1}`;
  nameInput.maxLength = 20;
  nameInput.style.cssText = 'padding:8px;background:var(--color-bg-primary);border:1px solid var(--border-color);border-radius:4px;color:var(--color-text-primary);font-family:var(--font-ui);font-size:14px;';
  nameInput.select();

  nameInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      (dialog.body.parentElement?.querySelector('.dialog__footer .btn--primary') as HTMLElement)?.click();
    }
  });

  errorEl.style.cssText = 'color:var(--color-danger);font-size:12px;min-height:16px;';

  const hint = document.createElement('div');
  hint.style.cssText = 'font-size:11px;color:var(--color-text-muted);';
  hint.textContent = state.staff.find((s) => s.isCEO && !s.teamId)
    ? 'Your CEO will be auto-assigned to this team.'
    : '';

  form.appendChild(label);
  form.appendChild(nameInput);
  form.appendChild(errorEl);
  form.appendChild(hint);
  dialog.body.appendChild(form);

  dialog.mount(parent);
}
